import React from "react";
import { Typography } from "@mui/joy";
import Card from "@mui/joy/Card";
import Accordion from "@mui/joy/Accordion";
import AccordionSummary from "@mui/joy/AccordionSummary";
import AccordionDetails from "@mui/joy/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";

/**
 * Deep comparison function for values
 */
const checkMatch = (val1, val2) => {
  if (val1 === val2) return true;
  if (val1 == null && val2 == null) return true;
  if (val1 == null || val2 == null) return false;
  if (
    typeof val1 === "object" &&
    typeof val2 === "object"
  ) {
    return JSON.stringify(val1) === JSON.stringify(val2);
  }
  return val1 == val2;
};

/**
 * Render a primitive value
 */
const renderPrimitiveValue = (value, placeholder = "No Data") => {
  if (value === null || value === undefined || value === "") {
    return (
      <Typography level="body-sm" color="neutral">
        {placeholder}
      </Typography>
    );
  }
  if (typeof value === "boolean") {
    return (
      <Typography level="body-sm">
        {value ? "True" : "False"}
      </Typography>
    );
  }
  return (
    <Typography
      level="body-sm"
      component="pre"
      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
    >
      {String(value)}
    </Typography>
  );
};

/**
 * RecursiveItemsComparison - Recursively compares extracted data vs ground truth
 * and displays match/mismatch indicators for each sub-field.
 */
const RecursiveItemsComparison = ({
  extracted,
  groundTruth,
  depth = 0,
}) => {
  // Handle null/undefined cases
  if (extracted == null && groundTruth == null) {
    return (
      <Typography level="body-sm" color="neutral">
        No Data Available
      </Typography>
    );
  }

  // Both are arrays
  if (Array.isArray(extracted) || Array.isArray(groundTruth)) {
    const extractedArr = Array.isArray(extracted) ? extracted : [];
    const groundTruthArr = Array.isArray(groundTruth) ? groundTruth : [];
    const maxLength = Math.max(extractedArr.length, groundTruthArr.length);

    if (maxLength === 0) {
      return (
        <Typography level="body-sm" color="neutral">
          Empty Array
        </Typography>
      );
    }

    return (
      <>
        {Array.from({ length: maxLength }).map((_, index) => {
          const extractedItem = extractedArr[index];
          const groundTruthItem = groundTruthArr[index];
          const isMatch = checkMatch(extractedItem, groundTruthItem);
          const isMissingExtracted = extractedItem === undefined;
          const isMissingGroundTruth = groundTruthItem === undefined;

          return (
            <Accordion key={index} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: "100%" }}
                  spacing={1}
                >
                  <Typography level="body-sm">
                    <strong>{index}</strong>
                    {isMissingExtracted && (
                      <Typography component="span" color="warning" sx={{ ml: 1 }}>
                        (missing in extracted)
                      </Typography>
                    )}
                    {isMissingGroundTruth && (
                      <Typography component="span" color="warning" sx={{ ml: 1 }}>
                        (missing in ground truth)
                      </Typography>
                    )}
                  </Typography>
                  {isMatch ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {typeof extractedItem === "object" ||
                typeof groundTruthItem === "object" ? (
                  <RecursiveItemsComparison
                    extracted={extractedItem}
                    groundTruth={groundTruthItem}
                    depth={depth + 1}
                  />
                ) : (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography level="body-xs" color="primary" sx={{ mb: 0.5 }}>
                        Extracted
                      </Typography>
                      {renderPrimitiveValue(extractedItem, "Missing")}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography level="body-xs" color="success" sx={{ mb: 0.5 }}>
                        Ground Truth
                      </Typography>
                      {renderPrimitiveValue(groundTruthItem, "Missing")}
                    </Grid>
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </>
    );
  }

  // Both are objects (not arrays)
  if (
    typeof extracted === "object" ||
    typeof groundTruth === "object"
  ) {
    const extractedObj = extracted && typeof extracted === "object" ? extracted : {};
    const groundTruthObj = groundTruth && typeof groundTruth === "object" ? groundTruth : {};
    
    // Get all unique keys from both objects
    const allKeys = [
      ...new Set([
        ...Object.keys(extractedObj),
        ...Object.keys(groundTruthObj),
      ]),
    ];

    if (allKeys.length === 0) {
      return (
        <Typography level="body-sm" color="neutral">
          Empty Object
        </Typography>
      );
    }

    return (
      <>
        {allKeys.map((key) => {
          const extractedValue = extractedObj[key];
          const groundTruthValue = groundTruthObj[key];
          const isMatch = checkMatch(extractedValue, groundTruthValue);
          const isMissingExtracted = !(key in extractedObj);
          const isMissingGroundTruth = !(key in groundTruthObj);
          const isNestedObject =
            (extractedValue && typeof extractedValue === "object") ||
            (groundTruthValue && typeof groundTruthValue === "object");

          return (
            <Accordion key={key} defaultExpanded sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: "100%" }}
                  spacing={1}
                >
                  <Typography level="body-sm">
                    <strong>{key}</strong>
                    {isMissingExtracted && (
                      <Typography component="span" color="warning" sx={{ ml: 1 }}>
                        (missing in extracted)
                      </Typography>
                    )}
                    {isMissingGroundTruth && (
                      <Typography component="span" color="warning" sx={{ ml: 1 }}>
                        (missing in ground truth)
                      </Typography>
                    )}
                  </Typography>
                  {isMatch ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {isNestedObject ? (
                  <Card variant="outlined" sx={{ p: 1 }}>
                    <RecursiveItemsComparison
                      extracted={extractedValue}
                      groundTruth={groundTruthValue}
                      depth={depth + 1}
                    />
                  </Card>
                ) : (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography level="body-xs" color="primary" sx={{ mb: 0.5 }}>
                        Extracted
                      </Typography>
                      {renderPrimitiveValue(extractedValue, "Missing")}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography level="body-xs" color="success" sx={{ mb: 0.5 }}>
                        Ground Truth
                      </Typography>
                      {renderPrimitiveValue(groundTruthValue, "Missing")}
                    </Grid>
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </>
    );
  }

  // Both are primitives - show side by side
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography level="body-xs" color="primary" sx={{ mb: 0.5 }}>
          Extracted
        </Typography>
        {renderPrimitiveValue(extracted)}
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography level="body-xs" color="success" sx={{ mb: 0.5 }}>
          Ground Truth
        </Typography>
        {renderPrimitiveValue(groundTruth)}
      </Grid>
    </Grid>
  );
};

export default RecursiveItemsComparison;
